import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API_URL } from './Config';
import './YouTubeLinkingPage.css';

function YouTubeLinkingPage() {
  const [languages, setLanguages] = useState({});
  const [loading, setLoading] = useState(true);
  const [connectingLanguage, setConnectingLanguage] = useState(null);
  const [message, setMessage] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    fetchConnections();
    
    // Handle OAuth callback messages
    const success = searchParams.get('success');
    const error = searchParams.get('error');
    const language = searchParams.get('language');
    const channel = searchParams.get('channel');
    const errorMessage = searchParams.get('message');

    if (success && language && channel) {
      setMessage({
        type: 'success',
        text: `Successfully connected ${decodeURIComponent(channel)} for ${language}`
      });
      // Clear URL parameters
      navigate('/youtube-linking', { replace: true });
      // Refresh connections
      setTimeout(() => fetchConnections(), 1000);
    } else if (error) {
      setMessage({
        type: 'error',
        text: errorMessage ? decodeURIComponent(errorMessage) : 'Authentication failed'
      });
      // Clear URL parameters
      navigate('/youtube-linking', { replace: true });
    }
  }, [searchParams, navigate]);

  const fetchConnections = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/youtube/connections`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setLanguages(data.languages || {});
    } catch (error) {
      console.error("Error fetching YouTube connections:", error);
      setMessage({
        type: 'error',
        text: 'Failed to load YouTube connections'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async (languageCode) => {
    try {
      setConnectingLanguage(languageCode);
      setMessage(null);

      const response = await fetch(`${API_URL}/youtube/auth/start/${languageCode}`, {
        method: 'POST'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Redirect to Google OAuth
      window.location.href = data.auth_url;

    } catch (error) {
      console.error("Error starting YouTube authentication:", error);
      setMessage({
        type: 'error',
        text: 'Failed to start YouTube authentication'
      });
    } finally {
      setConnectingLanguage(null);
    }
  };

  const handleDisconnect = async (languageCode, channelId) => {
    if (!window.confirm('Are you sure you want to disconnect this YouTube channel?')) {
      return;
    }

    try {
      const url = channelId 
        ? `${API_URL}/youtube/connection/${languageCode}?channel_id=${channelId}`
        : `${API_URL}/youtube/connection/${languageCode}`;
      
      const response = await fetch(url, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setMessage({
        type: 'success',
        text: `Successfully disconnected YouTube channel for ${languageCode}`
      });

      // Refresh connections
      fetchConnections();

    } catch (error) {
      console.error("Error disconnecting YouTube channel:", error);
      setMessage({
        type: 'error',
        text: 'Failed to disconnect YouTube channel'
      });
    }
  };

  const dismissMessage = () => {
    setMessage(null);
  };

  if (loading) {
    return (
      <div className="youtube-linking-container">
        <div className="youtube-linking-header">
          <button onClick={() => navigate('/')} className="back-button">
            ← Back to Projects
          </button>
          <h1>YouTube Channel Linking</h1>
        </div>
        <div className="loading">Loading YouTube connections...</div>
      </div>
    );
  }

  return (
    <div className="youtube-linking-container">
      <div className="youtube-linking-header">
        <button onClick={() => navigate('/')} className="back-button">
          ← Back to Projects
        </button>
        <h1>YouTube Channel Linking</h1>
        <p className="subtitle">Connect YouTube channels for different languages to enable automatic uploads</p>
      </div>

      {message && (
        <div className={`message ${message.type}`}>
          <span>{message.text}</span>
          <button onClick={dismissMessage} className="dismiss-button">×</button>
        </div>
      )}

      <div className="languages-grid">
        {Object.entries(languages).map(([languageCode, languageData]) => (
          <div key={languageCode} className="language-card">
            <div className="language-info">
              <h3>{languageData.language_name}</h3>
              <span className="language-code">({languageCode})</span>
            </div>

            {languageData.connected ? (
              <div className="connected-state">
                <div className="channel-info">
                  <div className="status-indicator connected"></div>
                  <div className="channel-details">
                    <strong>{languageData.channel.name}</strong>
                    <a 
                      href={languageData.channel.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="channel-link"
                    >
                      View Channel →
                    </a>
                    
                    {/* Analytics Display */}
                    {languageData.channel.analytics && (
                      <div className="channel-analytics">
                        {languageData.channel.analytics.error ? (
                          <div className="analytics-error">
                            <span className="error-icon">⚠️</span>
                            <span className="error-text">{languageData.channel.analytics.error}</span>
                            <button
                              onClick={() => handleConnect(languageCode)}
                              className="reconnect-button"
                              disabled={connectingLanguage === languageCode}
                            >
                              {connectingLanguage === languageCode ? 'Reconnecting...' : 'Reconnect'}
                            </button>
                          </div>
                        ) : (
                          <>
                            <div className="analytics-item">
                              <span className="analytics-label">👥 Subscribers:</span>
                              <span className="analytics-value">
                                {languageData.channel.analytics.subscriber_count.toLocaleString()}
                              </span>
                            </div>
                            <div className="analytics-item">
                              <span className="analytics-label">🎥 Videos:</span>
                              <span className="analytics-value">
                                {languageData.channel.analytics.video_count.toLocaleString()}
                              </span>
                            </div>
                            {languageData.channel.analytics.unread_comments > 0 && (
                              <div className="analytics-item highlight">
                                <span className="analytics-label">💬 New Comments:</span>
                                <span className="analytics-value unread-count">
                                  {languageData.channel.analytics.unread_comments}
                                </span>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                    
                    {languageData.channel.connected_at && (
                      <small>
                        Connected: {new Date(languageData.channel.connected_at).toLocaleDateString()}
                      </small>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => handleDisconnect(languageCode, languageData.channel.id)}
                  className="disconnect-button"
                >
                  Disconnect
                </button>
              </div>
            ) : (
              <div className="disconnected-state">
                <div className="status-indicator disconnected"></div>
                <button
                  onClick={() => handleConnect(languageCode)}
                  disabled={connectingLanguage === languageCode}
                  className="connect-button"
                >
                  {connectingLanguage === languageCode 
                    ? 'Connecting...' 
                    : '🔗 Connect Google Account'
                  }
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="instructions">
        <h3>Instructions:</h3>
        <ul>
          <li>Click "Connect Google Account" for each language you want to support</li>
          <li>You'll be redirected to Google to authorize YouTube access</li>
          <li>Select the YouTube channel you want to associate with that language</li>
          <li>Once connected, videos can be automatically uploaded to the appropriate channel based on their language</li>
        </ul>
        
        <div className="note">
          <strong>Note:</strong> You can connect the same YouTube channel to multiple languages, or use different channels for each language.
        </div>
      </div>
    </div>
  );
}

export default YouTubeLinkingPage;